// Pure Bingli import logic. NO redux / RTK / auth.api imports — so this module is safe to load
// from contexts (e.g. the e2e harness) that don't bring up the full Preventi Redux store.

import { DecryptedContact, DecryptedContent, DecryptedPatient, DecryptedSubContact, Identifier, ServiceLink } from '@icure/cardinal-sdk/model.mjs'
import { loadFromIterator } from './utils'
import { AccessLevel, CardinalSdk, CodeStub, DecryptedForm, DecryptedService, User } from '@icure/cardinal-sdk'
import { PatientFilters } from '@icure/cardinal-sdk/filters.mjs'
import { dateToFuzzyDate } from '@icure/form/utils/dates'
import { Field, Form } from '@icure/form/components/model'
import { getFields } from '../../helpers/fields'
import { answerLabelToLabelPromise, languages, questionCodeToLabelPromise, questionLabelToLabelPromise, questionsCodexPromise } from '../../constants/lib'
import { resolveFormTemplate, resolveLatestTemplateId } from './formTemplateUtils'
import { PatientsTagsEnum } from '../../helpers/types'

interface SurveyInfo {
  surveyId: string
  progression: string
  reasonOfVisit?: string
  createdAt: string
  patientId: string
  status: string
}

interface Answer {
  questionCode: string
  questionLabel: string
  answerValues: {
    choiceCode: string
    choiceLabel: string
  }[]
}

interface SurveyDetails {
  answers: Answer[]
}

export interface QuestionReference {
  label: { en: string; fr: string; nl: string }
  skipped: boolean
  unlinked: boolean
  options: { [code: string]: { en: string; fr: string; nl: string; code: string } }
  fields: string[]
}

export type BingliImportPhase = 'connecting' | 'fetching-surveys' | 'matching-patients' | 'importing-surveys'

export interface BingliImportProgress {
  phase: BingliImportPhase
  done: number
  total: number
  // Set while Bingli throttles us: how long the import waits before retrying.
  waitingMs?: number
}

export interface BingliImportParams {
  language: string
  // Restrict the import to these iCure patients (matched by id). Combined with `emails` (OR).
  patientIds?: string[]
  // Restrict the import to the iCure patients having one of these email addresses. Combined with `patientIds` (OR).
  emails?: string[]
  onProgress?: (progress: BingliImportProgress) => void
}

export type AnswerOutcomeKind =
  | 'applied'
  | 'guessed'
  | 'skipped'
  | 'unlinked-codex'
  | 'unknown-question'
  | 'no-field-in-template'
  | 'ambiguous-field'
  | 'conflict-id'
  | 'no-option-match'
  | 'missing-option-code'

export interface AnswerOutcome {
  surveyId: string
  patientId: string
  questionCode: string
  questionLabel: string
  iCureField?: string
  matchedOptionCodes: string[]
  rawChoices: { choiceCode: string; choiceLabel: string }[]
  kind: AnswerOutcomeKind
  detail?: string
}

export interface FormSummary {
  formId: string
  patientId: string
  surveyId: string
  formTemplateId?: string
  serviceCount: number
  subContactServiceLinkCount: number
  reFetchedFormPresent: boolean
  reFetchedSubContactLinksFormToServices: boolean
}

export interface BingliImportDiagnostics {
  surveysFetched: number
  surveysProcessed: number
  patientsMatched: number
  patientsMissing: string[]
  forms: FormSummary[]
  answerOutcomes: AnswerOutcome[]
  unmappedQuestionCodes: { questionCode: string; questionLabel: string; reason: string }[]
  errors: string[]
}

export interface BingliImportResult {
  forms: DecryptedForm[]
  errors: string[]
  diagnostics: BingliImportDiagnostics
}

type SdkApis = Awaited<ReturnType<typeof CardinalSdk.initialize>>
export interface RunBingliImportApis {
  contact: SdkApis['contact']
  form: SdkApis['form']
  healthcareParty: SdkApis['healthcareParty']
  patient: SdkApis['patient']
}

const NORMALIZE_STRIP = new RegExp('[\\s\\u0300-\\u036f]', 'g')
const NORMALIZE_QUOTES = new RegExp('[\'`’‘"”“]', 'g')

const BINGLI_IDENTIFIER_SYSTEM = 'be.bingli.identifier'

function bingliIdentifierOf(pat: DecryptedPatient): string | undefined {
  return pat.identifier.find((i) => i.system === BINGLI_IDENTIFIER_SYSTEM)?.value
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

function patientEmails(pat: DecryptedPatient): string[] {
  return (pat.addresses ?? []).flatMap((a) => (a.telecoms ?? []).map((t) => t.telecomNumber).filter((n): n is string => !!n)).map(normalizeEmail)
}

function normalizeForCompare(s: string | undefined | null): string {
  if (!s) return ''
  return s.toLowerCase().normalize('NFD').replace(NORMALIZE_QUOTES, '').replace(NORMALIZE_STRIP, '')
}

async function fetchBingliToken(creds: { email: string; password: string; tenantId: string; healthcareServiceId: string }): Promise<string> {
  const response = await fetch('https://boss.api.bingli.eu/security-api/token', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({
      credentialType: 10,
      account: creds.email,
      credential: creds.password,
      tenantId: creds.tenantId,
      healthcareServiceId: creds.healthcareServiceId,
    }),
  })
  const json = await response.json()
  const token = json?.data?.token
  if (!token) {
    throw new Error(`Bingli token request failed: ${JSON.stringify(json)}`)
  }
  return token as string
}

const BINGLI_MAX_RETRIES = 6
const BINGLI_MAX_WAIT_MS = 30000
const BINGLI_MAX_GAP_MS = 5000

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Pacing shared by all Bingli calls: once Bingli has throttled us, keep a minimum gap between requests
// (doubled on each throttle, slowly relaxed on success) instead of hitting the limit again on the next call.
const bingliPacing = { minGapMs: 0, lastCallAt: 0 }

async function pacedFetch(url: string, init: RequestInit): Promise<Response> {
  const wait = bingliPacing.lastCallAt + bingliPacing.minGapMs - Date.now()
  if (wait > 0) await sleep(wait)
  bingliPacing.lastCallAt = Date.now()
  return fetch(url, init)
}

// GET a Bingli resource, waiting and retrying while Bingli throttles us. A throttled answer can be a 429/503, or a network
// error when the gateway's throttling response carries no CORS headers (fetch then rejects without exposing the status).
// Any other non-2xx status throws, so that a rejected call is reported instead of being read as an empty answer.
async function getFromBingli<T>(token: string, url: string, onThrottled?: (waitMs: number) => void): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    let failure: string
    let retryAfterSeconds = 0
    try {
      const response = await pacedFetch(url, {
        method: 'GET',
        headers: { 'content-type': 'application/json', accept: 'application/json', authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        bingliPacing.minGapMs = Math.floor(bingliPacing.minGapMs * 0.9)
        return (await response.json()) as T
      }
      if (response.status !== 429 && response.status !== 503) throw new Error(`Bingli request ${url} failed with HTTP ${response.status}`)
      failure = `HTTP ${response.status}`
      retryAfterSeconds = Number(response.headers.get('retry-after')) || 0
    } catch (e) {
      if (!(e instanceof TypeError)) throw e
      failure = `network error (${e.message})`
    }
    if (attempt >= BINGLI_MAX_RETRIES) throw new Error(`Bingli request ${url} still failing after ${BINGLI_MAX_RETRIES} retries: ${failure}`)
    bingliPacing.minGapMs = Math.min(Math.max(bingliPacing.minGapMs * 2, 500), BINGLI_MAX_GAP_MS)
    const waitMs = Math.min(retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 1000 * 2 ** attempt, BINGLI_MAX_WAIT_MS)
    onThrottled?.(waitMs)
    await sleep(waitMs)
  }
}

async function fetchBingliSurveys(token: string, healthcareServiceId: string, onThrottled?: (waitMs: number) => void): Promise<SurveyInfo[]> {
  const json = await getFromBingli<SurveyInfo[]>(token, `https://fhir-gateway.api.bingli.eu/tenant-api/survey/healthcareservice/${healthcareServiceId}`, onThrottled)
  return json.filter((s) => s.progression === 'completed' && s.patientId)
}

async function fetchBingliSurveyDetails(token: string, surveyId: string, language: string, onThrottled?: (waitMs: number) => void): Promise<SurveyDetails> {
  return getFromBingli<SurveyDetails>(token, `https://fhir-gateway.api.bingli.eu/tenant-api/survey/${surveyId}?language=${language}`, onThrottled)
}

async function fetchBingliPatientEmail(token: string, patientId: string, onThrottled?: (waitMs: number) => void): Promise<string | undefined> {
  const json = await getFromBingli<{ email?: string }>(token, `https://fhir-gateway.api.bingli.eu/tenant-api/patients/${patientId}`, onThrottled)
  return json?.email ?? undefined
}

interface BuildServicesResult {
  services: DecryptedService[]
  outcomes: AnswerOutcome[]
  hardErrors: string[]
}

function buildServicesFromAnswers(args: {
  surveyId: string
  patientId: string
  hcpId: string
  patientLanguage: string
  fallbackLanguage: string
  ctcDate: number
  formTemplate: Form
  questionsCodex: Record<string, QuestionReference>
  questionCodeToLabel: Record<string, string>
  questionLabelToLabel: Record<string, string>
  answerLabelToLabel: Record<string, string[]>
  answers: Answer[]
}): BuildServicesResult {
  const {
    surveyId,
    patientId,
    hcpId,
    patientLanguage,
    fallbackLanguage,
    ctcDate,
    formTemplate,
    questionsCodex,
    questionCodeToLabel,
    questionLabelToLabel,
    answerLabelToLabel,
    answers,
  } = args

  const outcomes: AnswerOutcome[] = []
  const hardErrors: string[] = []

  const coalesced = Object.values(
    answers.reduce(
      (acc, answer) => {
        const ex = acc[answer.questionCode]
        return ex ? { ...acc, [answer.questionCode]: { ...ex, answerValues: [...ex.answerValues, ...answer.answerValues] } } : { ...acc, [answer.questionCode]: answer }
      },
      {} as { [id: string]: Answer },
    ),
  )

  const services = coalesced.reduce((result: DecryptedService[], answer) => {
    const rawChoices = answer.answerValues.map((a) => ({ choiceCode: a.choiceCode, choiceLabel: a.choiceLabel }))
    const recordOutcome = (kind: AnswerOutcomeKind, iCureField?: string, matchedOptionCodes: string[] = [], detail?: string) => {
      outcomes.push({ surveyId, patientId, questionCode: answer.questionCode, questionLabel: answer.questionLabel, iCureField, matchedOptionCodes, rawChoices, kind, detail })
    }

    // Strip Bingli's "None of these answers" sentinel (l_none) and empty-row padding from multi-selects.
    // These never carry information that maps to a Preventi.yaml option.
    answer = { ...answer, answerValues: answer.answerValues.filter((a) => a.choiceCode && a.choiceCode !== 'l_none') }
    if (answer.answerValues.length === 0) {
      recordOutcome('skipped', undefined, [], 'no meaningful choices (all empty or l_none)')
      return result
    }

    const q = questionsCodex[answer.questionCode]
    let primaryFieldName: string | undefined
    let primaryField: Field | undefined
    let matchingOptions: [string, string][]
    let kindHint: 'applied' | 'guessed'

    if (q) {
      if (q.skipped) {
        recordOutcome('skipped', undefined, [], 'codex marks question as skipped')
        return result
      }
      if (q.unlinked) {
        recordOutcome('unlinked-codex', undefined, [], 'codex marks question as unlinked')
        return result
      }
      primaryFieldName = q.fields?.[0]
      if (!primaryFieldName) {
        recordOutcome('unlinked-codex', undefined, [], 'codex entry has no fields[0]')
        return result
      }
      const found = getFields(formTemplate, primaryFieldName)
      if (!found.length) {
        recordOutcome('no-field-in-template', primaryFieldName, [], `Field ${primaryFieldName} from codex not found in current Preventi.yaml`)
        hardErrors.push(`Question with code ${answer.questionCode} not found in form template (codex pointed to '${primaryFieldName}')`)
        return result
      }
      primaryField = found[0]
      matchingOptions = Object.keys(q.options ?? {}).length ? answer.answerValues.map((a) => [q.options[a.choiceCode]?.code, a.choiceLabel] as [string, string]) : []
      kindHint = 'applied'
    } else {
      primaryFieldName = questionCodeToLabel[answer.questionCode] || questionLabelToLabel[answer.questionLabel]
      if (!primaryFieldName) {
        recordOutcome('unknown-question', undefined, [], 'no codex entry and no fallback label/code mapping')
        hardErrors.push(`Question with code ${answer.questionCode} [${answer.questionLabel}] not found`)
        return result
      }
      const fields = getFields(formTemplate, primaryFieldName)
      if (!fields.length) {
        recordOutcome('no-field-in-template', primaryFieldName, [], `Guessed field ${primaryFieldName} not in current Preventi.yaml`)
        hardErrors.push(`Question with code ${answer.questionCode} [${answer.questionLabel}] not found in form template`)
        return result
      }
      if (fields.length > 1) {
        hardErrors.push(`Question with code ${answer.questionCode} [${answer.questionLabel}] cannot be uniquely identified in form template`)
      }
      primaryField = fields[0]

      const options = Object.entries(primaryField.options ?? {}) as [string, string][]
      const answerLabels = answer.answerValues.map((a) => [...(answerLabelToLabel[a.choiceLabel] ?? []), a.choiceLabel])
      matchingOptions = options.length
        ? options.filter(([, option]) => answerLabels.some((label) => label.some((l) => normalizeForCompare(option) === normalizeForCompare(l))))
        : []
      kindHint = 'guessed'
    }

    // Group matchingOptions by target field prefix (so a single Bingli answer can fan
    // out into services on multiple iCure fields when the codex routes each choice differently).
    const validOptions = matchingOptions.filter(([c]) => !!c && c !== 'MISSING')
    const missingOptions = matchingOptions.filter(([c]) => !c || c === 'MISSING')
    if (missingOptions.length) {
      recordOutcome(
        'missing-option-code',
        primaryFieldName,
        validOptions.map((m) => m[0]),
        `Option codes still 'MISSING' for: ${missingOptions.map((m) => m[1]).join(', ')}`,
      )
    }

    const optionsByField = new Map<string, [string, string][]>()
    for (const opt of validOptions) {
      const prefix = opt[0].split('|')[0]
      const bucket = optionsByField.get(prefix) ?? []
      bucket.push(opt)
      optionsByField.set(prefix, bucket)
    }
    if (!optionsByField.has(primaryFieldName)) optionsByField.set(primaryFieldName, [])

    const primaryIsCompound = !!Object.entries(primaryField.options ?? {}).length
    if (primaryIsCompound && rawChoices.length > 0 && validOptions.length === 0 && missingOptions.length === 0) {
      recordOutcome('no-option-match', primaryFieldName, [], 'compound field but none of the answer choices matched a field option')
      return result
    }

    let added: DecryptedService[] = []
    for (const [targetFieldName, fieldOptions] of optionsByField.entries()) {
      const targetField = targetFieldName === primaryFieldName ? primaryField : getFields(formTemplate, targetFieldName)[0]
      if (!targetField) {
        recordOutcome('no-field-in-template', targetFieldName, [], `Codex option routes to field '${targetFieldName}' which is not in Preventi.yaml`)
        hardErrors.push(`Question ${answer.questionCode}: codex option targets unknown field '${targetFieldName}'`)
        continue
      }
      // Skip phantom primary buckets that ended up empty when every choice went elsewhere
      if (targetFieldName === primaryFieldName && fieldOptions.length === 0 && optionsByField.size > 1) continue

      const isCompound = !!Object.entries(targetField.options ?? {}).length
      // Dedupe by code id — CodeStub instances are objects, so a plain Set wouldn't catch duplicates,
      // and the Cardinal SDK rejects services whose codes contain repeated entries.
      const dedupedOptions = Array.from(new Map(fieldOptions.map((o) => [o[0], o])).values())
      const codes = isCompound ? dedupedOptions.map(([c]) => new CodeStub({ id: c, type: c.split('|')[0], code: c.split('|')[1], version: c.split('|')[2] })) : []
      const content = isCompound
        ? new DecryptedContent({
            compoundValue: dedupedOptions.map(([c]) => new DecryptedService({ label: c, content: { [patientLanguage]: new DecryptedContent({ booleanValue: true }) } })),
          })
        : new DecryptedContent(
            targetField.type === 'number-field'
              ? { numberValue: parseFloat(answer.answerValues[0]?.choiceLabel ?? '0') }
              : {
                  stringValue: answer.answerValues
                    ?.filter((a) => a.choiceCode !== 'l_none')
                    .map((a) => a.choiceLabel)
                    .join(', '),
                },
          )

      if (Number.isNaN(content.numberValue)) {
        console.warn(`${answer.questionCode} values ${answer.answerValues.map((av) => av.choiceLabel).join(',')} cannot be converted to number`)
        content.stringValue = answer.answerValues.map((av) => av.choiceLabel).join(',')
        content.numberValue = undefined
      }

      const id = `${hcpId}:${surveyId}-${targetFieldName}`

      const conflicting = [...result, ...added].find((s) => s.id === id)
      if (conflicting) {
        const conflictTag = conflicting.tags?.[0]?.code ?? 'unknown'
        recordOutcome('conflict-id', targetFieldName, [], `Service id '${id}' already in use by question ${conflictTag}`)
        hardErrors.push(`${conflictTag} conflicts with ${answer.questionCode}: both produce ${targetFieldName} as an ID`)
        continue
      }

      recordOutcome(kindHint, targetFieldName, codes.map((c) => c.id ?? '').filter(Boolean))

      added.push(
        new DecryptedService({
          id,
          label: targetFieldName,
          valueDate: ctcDate,
          codes,
          tags: [new CodeStub({ id: `BINGLI|${answer.questionCode}|1`, type: 'BINGLI', code: answer.questionCode, version: '1' })],
          content: { [patientLanguage || fallbackLanguage]: content },
        }),
      )
    }

    return [...result, ...added]
  }, [] as DecryptedService[])

  return { services, outcomes, hardErrors }
}

export async function runBingliImport(apis: RunBingliImportApis, user: User, params: BingliImportParams): Promise<BingliImportResult> {
  const { language, patientIds: restrictToPatientIds, emails, onProgress } = params
  const { contact: contactApi, form: formApi, healthcareParty: healthcarePartyApi, patient: patientApi } = apis

  const targetPatientIds = new Set(restrictToPatientIds ?? [])
  const targetEmails = new Set((emails ?? []).map(normalizeEmail))
  const isRestricted = targetPatientIds.size > 0 || targetEmails.size > 0
  const isTarget = (pat: DecryptedPatient) => !isRestricted || targetPatientIds.has(pat.id) || patientEmails(pat).some((e) => targetEmails.has(e))
  let lastProgress: BingliImportProgress = { phase: 'connecting', done: 0, total: 1 }
  const reportProgress = (phase: BingliImportPhase, done: number, total: number) => {
    lastProgress = { phase, done, total }
    onProgress?.(lastProgress)
  }
  const onThrottled = (waitMs: number) => onProgress?.({ ...lastProgress, waitingMs: waitMs })

  reportProgress('connecting', 0, 1)

  const diagnostics: BingliImportDiagnostics = {
    surveysFetched: 0,
    surveysProcessed: 0,
    patientsMatched: 0,
    patientsMissing: [],
    forms: [],
    answerOutcomes: [],
    unmappedQuestionCodes: [],
    errors: [],
  }
  const errors = diagnostics.errors

  const currentHcp = await healthcarePartyApi.getCurrentHealthcareParty()
  if (!currentHcp) {
    throw new Error('No current healthcare party found')
  }

  const bingliEmailAddress = currentHcp.properties.find((p) => p.id === 'bingliEmailAddress')?.typedValue?.stringValue
  const bingliCredentials = currentHcp.properties.find((p) => p.id === 'bingliCredentials')?.typedValue?.stringValue
  const bingliTenantId = currentHcp.properties.find((p) => p.id === 'bingliTenantId')?.typedValue?.stringValue
  const bingliHealthcareServiceId = currentHcp.properties.find((p) => p.id === 'bingliHealthcareServiceId')?.typedValue?.stringValue

  if (!bingliEmailAddress || !bingliCredentials || !bingliTenantId || !bingliHealthcareServiceId) {
    throw new Error('HCP is missing one of: bingliEmailAddress / bingliCredentials / bingliTenantId / bingliHealthcareServiceId')
  }

  const token = await fetchBingliToken({
    email: bingliEmailAddress,
    password: bingliCredentials,
    tenantId: bingliTenantId,
    healthcareServiceId: bingliHealthcareServiceId,
  })

  reportProgress('fetching-surveys', 0, 1)
  const surveys = await fetchBingliSurveys(token, bingliHealthcareServiceId, onThrottled)
  diagnostics.surveysFetched = surveys.length

  // Most recent survey first: the patient being imported has usually just completed theirs,
  // so a restricted import can stop after a few Bingli patient lookups.
  const patientIds = Array.from(new Set([...surveys].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)).map((s) => s.patientId)))

  const patients = (
    await loadFromIterator(
      await patientApi.filterPatientsBy(PatientFilters.byIdentifiersForSelf(patientIds.map((id) => new Identifier({ system: BINGLI_IDENTIFIER_SYSTEM, value: id })))),
      10000,
    )
  ).reduce(
    (acc, patient) => {
      const id = bingliIdentifierOf(patient)
      return id ? { ...acc, [id]: patient } : acc
    },
    {} as Record<string, DecryptedPatient>,
  )

  // Bingli patients not linked by identifier must be matched by email, one Bingli call each.
  // When the import is restricted, only the emails of selected patients not yet linked are looked for,
  // and the loop stops as soon as they have all been found.
  const pendingTargetEmails = new Set(targetEmails)
  Object.values(patients)
    .filter(isTarget)
    .forEach((pat) => patientEmails(pat).forEach((e) => pendingTargetEmails.delete(e)))
  const unmatchedPatientIds = isRestricted && pendingTargetEmails.size === 0 ? [] : patientIds.filter((id) => !patients[id])
  reportProgress('matching-patients', 0, unmatchedPatientIds.length)
  let matchedCount = 0
  for (const id of unmatchedPatientIds) {
    try {
      const email = await fetchBingliPatientEmail(token, id, onThrottled)
      const isPendingTarget = !!email && pendingTargetEmails.has(normalizeEmail(email))
      if (!isRestricted || isPendingTarget) {
        const candidates = email ? await loadFromIterator(await patientApi.filterPatientsBy(PatientFilters.byTelecomForSelf(email)), 10) : []
        // A patient already carrying a Bingli identifier is linked to another Bingli patient (it would have been matched by identifier otherwise).
        const unlinked = candidates.filter((p) => !bingliIdentifierOf(p))
        if (unlinked.length === 1) {
          const pat = unlinked[0]
          // Store the link right away, so that this and later imports match the patient by identifier without any email lookup.
          let linked = pat
          try {
            linked = await patientApi.modifyPatient(
              new DecryptedPatient({ ...pat, identifier: [...pat.identifier, new Identifier({ system: BINGLI_IDENTIFIER_SYSTEM, value: id })] }),
            )
          } catch (e) {
            errors.push(`Could not store Bingli identifier ${id} on patient ${pat.id}: ${e instanceof Error ? e.message : String(e)}`)
          }
          patients[id] = linked
          if (isTarget(linked)) patientEmails(linked).forEach((e) => pendingTargetEmails.delete(e))
        } else {
          // Several patients share this email (e.g. family members): linking one of them could file the survey in the wrong
          // medical record, and the stored identifier would make that permanent. Skip it instead.
          errors.push(
            unlinked.length > 1
              ? `Patient with identifier ${id} and email ${email}: ${unlinked.length} patients share this email, not imported to avoid filing the survey in the wrong record`
              : candidates.length
                ? `Patient with identifier ${id} and email ${email}: the matching patient(s) are already linked to another Bingli patient`
                : `Patient with identifier ${id} and email ${email ?? '<none>'} not found`,
          )
          diagnostics.patientsMissing.push(id)
          if (email && unlinked.length > 1) pendingTargetEmails.delete(normalizeEmail(email))
        }
      }
    } catch (e) {
      errors.push(`Patient lookup failed for ${id}: ${e instanceof Error ? e.message : String(e)}`)
      diagnostics.patientsMissing.push(id)
    }
    reportProgress('matching-patients', ++matchedCount, unmatchedPatientIds.length)
    if (isRestricted && pendingTargetEmails.size === 0) break
  }
  reportProgress('matching-patients', unmatchedPatientIds.length, unmatchedPatientIds.length)
  diagnostics.patientsMatched = Object.values(patients).filter(isTarget).length
  const targetSurveys = surveys.filter((s) => patients[s.patientId] && isTarget(patients[s.patientId]))

  const answerLabelToLabel = await answerLabelToLabelPromise
  const questionCodeToLabel = await questionCodeToLabelPromise
  const questionLabelToLabel = await questionLabelToLabelPromise
  const questionsCodex = await questionsCodexPromise
  const formTemplate = await resolveFormTemplate(formApi, undefined)
  const latestTemplateId = await resolveLatestTemplateId(formApi)

  if (!latestTemplateId) {
    errors.push('No Preventi form template on server; new forms will be created without formTemplateId, breaking computed values.')
  }

  const result: DecryptedForm[] = []

  reportProgress('importing-surveys', 0, targetSurveys.length)
  for (const survey of targetSurveys) {
    try {
      const pat = patients[survey.patientId]

      diagnostics.surveysProcessed += 1

      const surveyDetails = await fetchBingliSurveyDetails(token, survey.surveyId, languages[pat.languages[0] ?? 'fr'] ?? 'fr-be', onThrottled)

      const formId = `${currentHcp.id}:${survey.surveyId}`
      let iCureForm: DecryptedForm | undefined
      try {
        iCureForm = await formApi.getForm(formId)
      } catch (e) {
        errors.push(`getForm(${formId}) threw: ${e instanceof Error ? e.message : String(e)}`)
      }
      if (!iCureForm) {
        const formToCreate = new DecryptedForm({
          id: formId,
          descr: survey.reasonOfVisit,
          responsible: currentHcp.id,
          formTemplateId: latestTemplateId,
        })
        try {
          iCureForm = await formApi.createForm(
            await formApi.withEncryptionMetadata(formToCreate, pat, {
              user,
              delegates: Object.fromEntries(
                Object.values(pat.securityMetadata?.secureDelegations ?? [])
                  .map((d) => (d.delegate ? [d.delegate, AccessLevel.Write] : undefined))
                  .filter((x) => !!x),
              ),
            }),
          )
        } catch (e) {
          errors.push(`createForm(${formId}) threw: ${e instanceof Error ? e.message : String(e)}`)
        }
      }
      if (!iCureForm) {
        errors.push(`Form with id ${formId} not found and cannot be created`)
        continue
      }

      const ctcDate = dateToFuzzyDate(new Date(survey.createdAt))

      const { services, outcomes, hardErrors } = buildServicesFromAnswers({
        surveyId: survey.surveyId,
        patientId: survey.patientId,
        hcpId: currentHcp.id,
        patientLanguage: pat.languages?.[0] ?? language,
        fallbackLanguage: language,
        ctcDate,
        formTemplate,
        questionsCodex,
        questionCodeToLabel,
        questionLabelToLabel,
        answerLabelToLabel,
        answers: surveyDetails.answers,
      })

      diagnostics.answerOutcomes.push(...outcomes)
      errors.push(...hardErrors)

      const ctc = await contactApi.withEncryptionMetadata(
        new DecryptedContact({
          responsible: currentHcp.id,
          openingDate: ctcDate,
          services,
          subContacts: [new DecryptedSubContact({ formId: iCureForm.id, services: services.map((s) => new ServiceLink({ serviceId: s.id })) })],
        }),
        pat,
        { user },
      )
      const createdContact = await contactApi.createContact(ctc)

      if (pat.tags.some((t) => t.type === 'PREVENTI' && t.code === PatientsTagsEnum.QUESTIONNAIRE_SENT)) {
        await patientApi.modifyPatient(
          new DecryptedPatient({
            ...pat,
            tags: [
              ...pat.tags.filter((t) => t.type !== 'PREVENTI' || t.code !== PatientsTagsEnum.QUESTIONNAIRE_SENT),
              new CodeStub({ id: `PREVENTI|${PatientsTagsEnum.RESPONSE_RECEIVED}|1`, type: 'PREVENTI', code: PatientsTagsEnum.RESPONSE_RECEIVED, version: '1' }),
            ],
          }),
        )
      }
      result.push(iCureForm)

      let reFetchedFormPresent = false
      let reFetchedSubContactLinksFormToServices = false
      try {
        const fetched = await contactApi.getContact(createdContact.id)
        reFetchedFormPresent = !!fetched
        const sc = fetched?.subContacts?.find((s) => s.formId === iCureForm!.id)
        reFetchedSubContactLinksFormToServices = !!sc && (sc.services?.length ?? 0) > 0
      } catch (e) {
        errors.push(`Re-fetch of contact ${createdContact.id} failed: ${e instanceof Error ? e.message : String(e)}`)
      }

      diagnostics.forms.push({
        formId: iCureForm.id,
        patientId: survey.patientId,
        surveyId: survey.surveyId,
        formTemplateId: iCureForm.formTemplateId,
        serviceCount: services.length,
        subContactServiceLinkCount: services.length,
        reFetchedFormPresent,
        reFetchedSubContactLinksFormToServices,
      })
    } catch (error) {
      errors.push(`Error processing survey ${survey.surveyId}: ${error instanceof Error ? error.message : 'Unknown error'}`)
      console.error(`Error processing survey ${survey.surveyId}:`, error)
    } finally {
      reportProgress('importing-surveys', diagnostics.surveysProcessed, targetSurveys.length)
    }
  }

  const seenUnmapped = new Set<string>()
  for (const o of diagnostics.answerOutcomes) {
    if (o.kind === 'unknown-question' || o.kind === 'unlinked-codex' || o.kind === 'no-field-in-template' || o.kind === 'no-option-match' || o.kind === 'missing-option-code') {
      const key = `${o.questionCode}|${o.kind}`
      if (!seenUnmapped.has(key)) {
        seenUnmapped.add(key)
        diagnostics.unmappedQuestionCodes.push({ questionCode: o.questionCode, questionLabel: o.questionLabel, reason: o.kind + (o.detail ? `: ${o.detail}` : '') })
      }
    }
  }

  return { forms: result, errors, diagnostics }
}
